/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, HostBinding, AfterViewInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { AmandaOptions } from 'shared/services/assets-shared.service';

import { SliceBaseHostStyles, SliceBaseRenderComponent } from 'extensions/core/slice-base/render/slice-base-render.component';


interface SliceFlexHostStyles extends SliceBaseHostStyles {
  justifyContent: string;
  alignItems: string;
  alignContent: string;
}

function toFlex(value: string): string {
  switch (value) {
    case 'start': return 'flex-start';
    case 'end': return 'flex-end';
    case 'spaceBetween': return 'space-between';
    case 'spaceAround': return 'space-around';
    case 'spaceEvenly': return 'space-evenly';
    default: return value;
  }
}

@Component({
  selector: 'slice-flex-render',
  templateUrl: './slice-flex-render.component.html',
  styleUrls: ['./slice-flex-render.component.scss']
})
export class SliceFlexRenderComponent extends SliceBaseRenderComponent implements AfterViewInit {

  @HostBinding('style.justify-content')
  public justifyContent!: string;

  @HostBinding('style.align-items')
  public alignItems!: string;

  @HostBinding('style.align-content')
  public alignContent!: string;

  @ViewChild('image') public image?: ElementRef<HTMLImageElement>;
  public canBeClosed: boolean = false;
  public deletedSlices: string[] = [];

  @HostBinding('class')
  public get classes(): string {
    let classes: string = (this.slice?.settings?.styles?.shadow && this.slice.settings.styles.shadow !== 'none') ? `${this.slice.settings.styles?.shadow} ` : '';

    if (this.slice?.settings?.styles?.borderRadiusType) {
      if (this.slice.settings.styles.borderRadiusType === 'straight') {
        classes += 'border-radius-straight';
      } else if (this.slice.settings.styles.borderRadiusType === 'semi-round') {
        classes += 'border-radius-semi-round';
      } else if (this.slice.settings.styles.borderRadiusType === 'round') {
        classes += 'border-radius-round';
      }
    }

    return classes + ((this.slice?.settings?.general.classes as string) ? ` ${this.slice?.settings?.general.classes}` : '');
  }

  @HostBinding('style.cursor')
  public get cursorType(): string {
    return this.hasCustomCursorIcon && !this.slice?.settings?.general?.customCursor?.showPointer ? 'none' : 'inherit';
  }

  @HostBinding('style.flex-direction')
  public get flexDirection(): string {
    return this.slice?.settings?.general?.flexDirection ?? 'column';
  }

  @HostBinding('style.flex-wrap')
  public get flexWrap(): string {
    return this.slice?.settings?.general?.flexWrap ?? 'initial';
  }

  @HostBinding('class.hidden')
  public get display(): boolean {
    return this.slots.filter(slot => !slot.hidden).length === 0;
  }

  @HostBinding('attr.data-composition-id')
  public get compositionId(): string | undefined {
    const compositionId = this.slice?.settings?.general?.compositionId;
    return compositionId !== '' ? compositionId : undefined;
  }

  @HostBinding('style.opacity')
  public get opacity(): number {
    const opacity = this.slice?.settings?.styles?.opacity;
    return opacity !== '' ? opacity / 100 : 1;
  }

  @HostListener('mouseenter') public onEnter() {
    if (this.image) {
      this.image.nativeElement.style.display = 'block';
      this.image.nativeElement.style.pointerEvents = 'none';
      this.el.nativeElement.addEventListener('pointermove', this.imageFollowMouse);
    }
  }

  @HostListener('mouseleave') public onLeave() {
    if (this.image) {
      this.image.nativeElement.style.display = '';
      this.image.nativeElement.style.pointerEvents = '';
      this.el.nativeElement.removeEventListener('pointermove', this.imageFollowMouse);
    }
  }

  public get hasCustomCursorIcon(): boolean {
    return !!this.slice?.settings?.general?.customCursor?.showImage
      && !!this.slice?.settings?.general?.customCursor?.image?.image;
  }

  public get customCursorIcon(): string | undefined {
    const iconUrl = this.slice?.settings?.general?.customCursor?.image?.image;
    const assetsUrl = this.renderConfigurationService.config.assetsUrl;
    const akamaiOptions: AmandaOptions = { isWidthParamEnabled: false };

    return this.assetsSharedService.getAssetUrl(iconUrl, assetsUrl, akamaiOptions);
  }

  public trackByIndex(index: number): number {
    return index;
  }

  public ngAfterViewInit() {
    if (this.slice?.settings?.general.closeSection) {
      this.canBeClosed = true;

      if (this.slice?.settings?.general.keepCloseSection && this.renderStorageService.exists('deletedSlices', 'sessionstorage')) {
        const filterDeletedSlices = this.renderStorageService.getItem('deletedSlices', 'sessionstorage')!;
        if (filterDeletedSlices.includes(this.slice.id)) {
          this.deleteSlice(false);
        }
      }
    }
  }

  public deleteSlice(addToStorage: boolean = true) {
    this.slots = [];
    this.canBeClosed = false;
    this.el.nativeElement.remove();
    if (addToStorage && this.slice?.settings?.general.keepCloseSection) {
      if (this.renderStorageService.exists('deletedSlices', 'sessionstorage')) {
        const deletedSlices: string = this.renderStorageService.getItem('deletedSlices', 'sessionstorage')!;
        this.deletedSlices = JSON.parse(deletedSlices);
      }
      this.deletedSlices.push(this.slice.id);
      this.renderStorageService.insert('deletedSlices', JSON.stringify(this.deletedSlices), 'sessionstorage');
    }
  }

  protected onSliceInit(): void { /* empty */ }

  protected setDefaultHostStyles(): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings.styles.bgColor,
      justifyContent: this.slice.settings.general.justifyContent,
      alignItems: this.slice.settings.general.alignItems,
      alignContent: this.slice.settings.general.alignContent,
      marginTop: this.slice.settings.styles.marginTop,
      marginRight: this.slice.settings.styles.marginRight,
      marginLeft: this.slice.settings.styles.marginLeft,
      marginBottom: this.slice.settings.styles.marginBottom,
      paddingTop: this.slice.settings.styles.paddingTop,
      paddingRight: this.slice.settings.styles.paddingRight,
      paddingLeft: this.slice.settings.styles.paddingLeft,
      paddingBottom: this.slice.settings.styles.paddingBottom
    });
  }

  protected setResponsiveHostStyles(
    breakpointStyles: ResponsiveSetting,
    defaultBpSettings: Partial<ResponsiveSetting>
  ): void {
    this.setHostStyles({
      backgroundColor: this.slice.settings.styles.bgColor,
      justifyContent: this.slice.settings.general.justifyContent,
      alignItems: this.slice.settings.general.alignItems,
      alignContent: this.slice.settings.general.alignContent,
      marginTop: breakpointStyles.marginTop || defaultBpSettings.marginTop || '',
      marginRight: breakpointStyles.marginRight || defaultBpSettings.marginRight || '',
      marginLeft: breakpointStyles.marginLeft || defaultBpSettings.marginLeft || '',
      marginBottom: breakpointStyles.marginBottom || defaultBpSettings.marginBottom || '',
      paddingTop: breakpointStyles.paddingTop || defaultBpSettings.paddingTop || '',
      paddingRight: breakpointStyles.paddingRight || defaultBpSettings.paddingRight || '',
      paddingLeft: breakpointStyles.paddingLeft || defaultBpSettings.paddingLeft || '',
      paddingBottom: breakpointStyles.paddingBottom || defaultBpSettings.paddingBottom || ''
    });
  }

  private setHostStyles(styles: SliceFlexHostStyles): void {
    this.backgroundColor = styles.backgroundColor;
    this.justifyContent = toFlex(styles.justifyContent);
    this.alignItems = toFlex(styles.alignItems);
    this.alignContent = toFlex(styles.alignContent);
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
    this.paddingTop = styles.paddingTop;
    this.paddingRight = styles.paddingRight;
    this.paddingLeft = styles.paddingLeft;
    this.paddingBottom = styles.paddingBottom;
  }

  private readonly imageFollowMouse = (event: PointerEvent): void => {
    const pieceDimensions = this.el.nativeElement.getBoundingClientRect();
    const posX = event.clientX - pieceDimensions.x - (this.image!.nativeElement.offsetWidth / 2);
    const posY = event.clientY - pieceDimensions.y - (this.image!.nativeElement.offsetHeight / 2);

    this.image!.nativeElement.style.left = `${posX}px`;
    this.image!.nativeElement.style.top = `${posY}px`;
  };

}
